/**
 * Site footer entry.
 *
 * Importing the SCSS here makes Vite bundle the block's CSS alongside its JS,
 * so the theme ships both together.
 *
 * The sign-up works without this file: it is an ordinary POST to admin-post.php
 * that redirects back to the same page with the outcome in the query. All this
 * does is spare the visitor that round trip — the same request, sent with
 * fetch, answered as JSON by the same handler. A network failure hands the
 * submission back to the browser rather than swallowing it.
 *
 * Field validation stays the browser's (`type="email"`, `required`), which is
 * both lighter and better localised than anything written here would be.
 *
 * A footer posting to an outside mailing provider has no status region and is
 * left alone: that response is not ours to read, and intercepting it would only
 * break a form that already works.
 */

import './index.scss';

const form = document.querySelector('.site-footer__signup');
const status = document.querySelector('.site-footer__signup-status');
const submit = form?.querySelector('.site-footer__signup-submit');

if (form && status && submit) {
    const messages = {
        sent: status.dataset.sent ?? '',
        error: status.dataset.error ?? '',
    };

    /*
     * `form.action` is NOT the URL here. admin-post.php needs a field called
     * `action`, and a named control shadows the form property of the same name,
     * so `form.action` hands back that <input> instead. Reading the attribute is
     * what keeps this pointing at admin-post.php rather than at an element.
     */
    const endpoint = form.getAttribute('action') ?? '';

    /**
     * Put one of the two server-supplied messages into the live region.
     *
     * @param {string} state Either `sent` or `error`.
     * @returns {void}
     */
    const report = (state) => {
        status.dataset.state = state;
        status.textContent = messages[state] ?? '';
    };

    form.addEventListener('submit', async (event) => {
        // Let the browser show its own message for an empty or malformed field.
        if (!form.reportValidity()) {
            return;
        }

        event.preventDefault();

        submit.disabled = true;
        form.setAttribute('aria-busy', 'true');

        /* Cleared before the request, not after: leaving the previous answer up
         * while a new one is in flight reads as though nothing was sent. */
        status.dataset.state = '';
        status.textContent = '';

        try {
            const response = await fetch(endpoint, {
                method: 'POST',
                body: new FormData(form),
                headers: { 'X-Requested-With': 'XMLHttpRequest' },
                credentials: 'same-origin',
            });
            const payload = await response.json();

            if (response.ok && payload.ok) {
                form.reset();
                report('sent');
            } else {
                report('error');
            }
        } catch {
            /* The network, not the answer, failed: hand it back to the browser
             * so the submission is not lost to a dead button. Called off the
             * prototype for the same reason `endpoint` exists — a control named
             * `submit` would otherwise shadow the method. */
            HTMLFormElement.prototype.submit.call(form);
            return;
        }

        submit.disabled = false;
        form.removeAttribute('aria-busy');
    });
}
